/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * Dealer Map JavaScript
 * Loftsails - Leaflet.js Integration
 */

(function() {
  'use strict';

  function init() {

  // Get dealers from global variable (set by Liquid)
  const dealers = window.loftsailsDealers || [];

  const strings = window.loftsailsDealerStrings;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  let returnFocus = null;
  let searchTimeout;
  const lifetime = new AbortController();

  // DOM Elements
  const mapContainer = document.getElementById('dealerMap');
  const searchInput = document.getElementById('dealerSearchInput');
  const locateBtn = document.getElementById('dealerLocateBtn');
  const resetBtn = document.getElementById('dealerResetView');
  const dealerList = document.getElementById('dealerList');
  const modal = document.getElementById('dealerModal');
  const modalClose = document.getElementById('dealerModalClose');
  const filterChips = document.querySelectorAll('.dealer-map__filter-chip');

  // Exit if no map container
  if (!mapContainer || dealers.length === 0) {
    console.warn('Dealer map: No container or dealers found');
    return;
  }

  if (mapContainer.dealerDestroy) return;
  const section = mapContainer.closest('.shopify-section');
  mapContainer.dealerDestroy = () => {
    if (modal?.classList.contains('active')) closeModal();
    lifetime.abort();
    clearTimeout(searchTimeout);
    map.remove();
    delete mapContainer.dealerDestroy;
  };
  section?.addEventListener('shopify:section:unload', mapContainer.dealerDestroy, { once: true, signal: lifetime.signal });

  // Sort dealers by country then name
  dealers.sort((a, b) => {
    if (a.country === b.country) {
      return a.name.localeCompare(b.name);
    }
    return a.country.localeCompare(b.country);
  });

  // Initialize map - World view by default
  const map = L.map('dealerMap', {
    center: [20, 0],
    zoom: 2,
    zoomControl: false
  });

  // Leaflet's default zoom buttons are titled "Zoom in" / "Zoom out" in English;
  // pass the storefront strings so the FR page reads them in French.
  L.control.zoom({
    position: 'topleft',
    zoomInTitle: strings?.zoomIn || 'Zoom in',
    zoomOutTitle: strings?.zoomOut || 'Zoom out'
  }).addTo(map);

  // Add tile layer (OpenStreetMap — no API key required)
  // DO NOT revert to CartoDB (basemaps.cartocdn.com): CartoDB now requires an API key
  // and causes an "API key required" error on /pages/where-to-buy.
  // OpenStreetMap standard tiles require no key. If you must change tiles, use a
  // key-free provider only (e.g. OpenStreetMap), never Mapbox/Google/Carto without
  // a verified API key in settings.
  L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
    attribution: '&copy; OpenStreetMap contributors',
    maxZoom: 19
  }).addTo(map);

  // Custom marker icon
  const dealerIcon = L.divIcon({
    className: 'dealer-marker',
    html: '<svg fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd"/></svg>',
    iconSize: [32, 32],
    iconAnchor: [16, 16]
  });

  // Store markers
  const markers = [];

  // Add markers for all dealers
  dealers.forEach(dealer => {
    if (dealer.lat && dealer.lng) {
      const marker = L.marker([dealer.lat, dealer.lng], { icon: dealerIcon })
        .addTo(map)
        .on('click', () => openModal(dealer));

      marker.dealerData = dealer;
      markers.push(marker);
    }
  });

  // Update counts
  function updateCounts(count) {
    const mapCount = document.getElementById('dealerMapCount');
    const listCount = document.getElementById('dealerListCount');
    const text = (count === 1 ? strings.countOne : strings.countOther).replace('__COUNT__', count);
    if (mapCount) mapCount.textContent = text;
    if (listCount) listCount.textContent = text;
  }

  // Render dealer list
  function renderDealerList(filteredDealers) {
    if (!dealerList) return;

    dealerList.innerHTML = '';

    // Group by country
    const grouped = {};
    filteredDealers.forEach(d => {
      if (!grouped[d.country]) grouped[d.country] = [];
      grouped[d.country].push(d);
    });

    // Sort countries alphabetically A-Z
    const sortedCountries = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

    sortedCountries.forEach(country => {
      // Country header
      const countryHeader = document.createElement('div');
      countryHeader.className = 'dealer-map__list-country';
      countryHeader.innerHTML = `
        <div class="dealer-map__list-country-inner">
          <span class="dealer-map__list-country-name">${country}</span>
          <span class="dealer-map__list-country-count">${grouped[country].length}</span>
        </div>
      `;
      dealerList.appendChild(countryHeader);

      // Dealers in country
      grouped[country].forEach(dealer => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'dealer-map__list-item';
        item.dataset.dealerId = dealer.id;
        const name = document.createElement('span');
        name.className = 'dealer-map__list-item-name'; name.textContent = dealer.name;
        const address = document.createElement('span');
        address.className = 'dealer-map__list-item-address'; address.textContent = dealer.address;
        item.append(name, address);
        item.addEventListener('click', () => {
          openModal(dealer);
          document.querySelectorAll('.dealer-map__list-item').forEach(el => el.classList.remove('active'));
          item.classList.add('active');
        });
        dealerList.appendChild(item);
      });
    });

    updateCounts(filteredDealers.length);
  }

  // Initial render
  renderDealerList(dealers);

  // Modal functions
  function openModal(dealer) {
    if (!modal) return;

    document.getElementById('modalDealerName').textContent = dealer.name;
    document.getElementById('modalDealerCountry').textContent = dealer.country;
    document.getElementById('modalDealerAddress').textContent = dealer.address;

    // Phone
    const phoneRow = document.getElementById('modalPhoneRow');
    const phoneEl = document.getElementById('modalDealerPhone');
    if (dealer.phone) {
      phoneEl.textContent = dealer.phone;
      phoneEl.href = `tel:${dealer.phone.replace(/\s/g, '')}`;
      phoneRow.classList.remove('hidden');
    } else {
      phoneRow.classList.add('hidden');
    }

    // Email
    const emailRow = document.getElementById('modalEmailRow');
    const emailEl = document.getElementById('modalDealerEmail');
    if (dealer.email) {
      emailEl.textContent = dealer.email;
      emailEl.href = `mailto:${dealer.email}`;
      emailRow.classList.remove('hidden');
    } else {
      emailRow.classList.add('hidden');
    }

    // Website (with UTM tracking)
    const websiteRow = document.getElementById('modalWebsiteRow');
    const websiteEl = document.getElementById('modalDealerWebsite');
    if (dealer.website) {
      const displayUrl = dealer.website.replace(/^https?:\/\//, '').replace(/\/$/, '');
      websiteEl.textContent = displayUrl;

      // Build URL with UTM parameters
      let baseUrl = dealer.website.startsWith('http') ? dealer.website : `https://${dealer.website}`;
      const utmParams = 'utm_source=loftsails&utm_medium=dealer-locator&utm_campaign=find-dealer';
      const separator = baseUrl.includes('?') ? '&' : '?';
      websiteEl.href = `${baseUrl}${separator}${utmParams}`;

      websiteRow.classList.remove('hidden');
    } else {
      websiteRow.classList.add('hidden');
    }

    // Social Media
    const socialRow = document.getElementById('modalSocialRow');
    const instagramEl = document.getElementById('modalDealerInstagram');
    const facebookEl = document.getElementById('modalDealerFacebook');
    let hasSocial = false;

    if (dealer.instagram) {
      const handle = dealer.instagram.replace('@', '');
      instagramEl.href = `https://instagram.com/${handle}`;
      instagramEl.textContent = `@${handle}`;
      instagramEl.classList.remove('hidden');
      hasSocial = true;
    } else {
      instagramEl.classList.add('hidden');
    }

    if (dealer.facebook) {
      facebookEl.href = `https://facebook.com/${dealer.facebook}`;
      facebookEl.textContent = dealer.facebook;
      facebookEl.classList.remove('hidden');
      hasSocial = true;
    } else {
      facebookEl.classList.add('hidden');
    }

    socialRow.classList.toggle('hidden', !hasSocial);

    // Google Maps links
    const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(dealer.address)}`;
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${dealer.lat},${dealer.lng}`;

    document.getElementById('modalDirections').href = directionsUrl;
    document.getElementById('modalOpenMaps').href = mapsUrl;

    returnFocus = document.activeElement;
    modal.inert = false;
    modal.classList.add('active');
    modalClose?.focus();

    // Center map on dealer
    map.flyTo([dealer.lat, dealer.lng], 12, { duration: reducedMotion.matches ? 0 : 0.5, animate: !reducedMotion.matches });
  }

  function closeModal() {
    if (!modal?.classList.contains('active')) return;
    returnFocus?.focus();
    modal.classList.remove('active');
    modal.inert = true;
  }

  // Modal event listeners
  if (modalClose) {
    modalClose.addEventListener('click', closeModal);
  }

  if (modal) {
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeModal();
    });
  }

  document.addEventListener('keydown', (e) => {
    if (!modal?.classList.contains('active')) return;
    if (e.key === 'Escape') { e.preventDefault(); closeModal(); }
    if (e.key === 'Tab') {
      const controls = [...modal.querySelectorAll('button, a[href]')].filter(el => el.getClientRects().length && !el.disabled);
      const first = controls[0], last = controls.at(-1);
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }
  }, { signal: lifetime.signal });

  // Region filter
  filterChips.forEach(chip => {
    chip.addEventListener('click', () => {
      filterChips.forEach(c => c.classList.remove('active'));
      chip.classList.add('active');

      const region = chip.dataset.region;
      let filtered;

      if (region === 'all') {
        filtered = dealers;
        map.flyTo([20, 0], 2, { duration: reducedMotion.matches ? 0 : 0.5, animate: !reducedMotion.matches }); // World view
      } else if (region === 'europe') {
        filtered = dealers.filter(d => d.region === 'europe');
        map.flyTo([50, 10], 4, { duration: reducedMotion.matches ? 0 : 0.5, animate: !reducedMotion.matches });
      } else if (region === 'americas') {
        filtered = dealers.filter(d => d.region === 'americas');
        map.flyTo([20, -80], 3, { duration: reducedMotion.matches ? 0 : 0.5, animate: !reducedMotion.matches });
      } else if (region === 'asia-pacific') {
        filtered = dealers.filter(d => d.region === 'asia-pacific');
        map.flyTo([20, 120], 3, { duration: reducedMotion.matches ? 0 : 0.5, animate: !reducedMotion.matches });
      } else {
        filtered = dealers.filter(d => d.region === region);
      }

      // Update markers
      markers.forEach(marker => {
        const dealer = marker.dealerData;
        if (region === 'all' || dealer.region === region) {
          marker.addTo(map);
        } else {
          marker.remove();
        }
      });

      renderDealerList(filtered);
    });
  });

  // Haversine distance calculation (km)
  function getDistance(lat1, lon1, lat2, lon2) {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return R * c;
  }

  // Find nearest dealers to a location
  function findNearestDealers(lat, lng, count = 5) {
    return dealers
      .map(d => ({ ...d, distance: getDistance(lat, lng, d.lat, d.lng) }))
      .sort((a, b) => a.distance - b.distance)
      .slice(0, count);
  }

  // Geocode a location query using Nominatim
  async function geocodeLocation(query) {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`, { signal: lifetime.signal });
      const data = await response.json();
      if (data && data.length > 0) {
        return { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) };
      }
    } catch (e) {
      console.warn('Geocoding failed:', e);
    }
    return null;
  }

  // Search
  if (searchInput) {

    searchInput.addEventListener('input', (e) => {
      clearTimeout(searchTimeout);
      searchTimeout = setTimeout(async () => {
        const query = e.target.value.toLowerCase().trim();

        if (query === '') {
          markers.forEach(m => m.addTo(map));
          renderDealerList(dealers);
          return;
        }

        // First try direct text search
        const filtered = dealers.filter(d =>
          d.name.toLowerCase().includes(query) ||
          d.address.toLowerCase().includes(query) ||
          d.country.toLowerCase().includes(query)
        );

        if (filtered.length > 0) {
          // Direct match found
          markers.forEach(marker => {
            const dealer = marker.dealerData;
            const matches = dealer.name.toLowerCase().includes(query) ||
                           dealer.address.toLowerCase().includes(query) ||
                           dealer.country.toLowerCase().includes(query);
            if (matches) {
              marker.addTo(map);
            } else {
              marker.remove();
            }
          });
          renderDealerList(filtered);
        } else {
          // No direct match - try geocoding to find nearest
          const location = await geocodeLocation(query);
          if (lifetime.signal.aborted) return;
          if (location) {
            const nearest = findNearestDealers(location.lat, location.lng, 10);
            markers.forEach(marker => {
              const isNear = nearest.some(n => n.id === marker.dealerData.id);
              if (isNear) {
                marker.addTo(map);
              } else {
                marker.remove();
              }
            });
            map.flyTo([location.lat, location.lng], 6, { duration: reducedMotion.matches ? 0 : 0.5, animate: !reducedMotion.matches });
            renderDealerList(nearest);
          } else {
            // Geocoding failed - show all
            markers.forEach(m => m.addTo(map));
            renderDealerList(dealers);
          }
        }

        // Reset filter chips
        filterChips.forEach(c => c.classList.remove('active'));
      }, 500);
    });
  }

  // Reset view
  if (resetBtn) {
    resetBtn.addEventListener('click', () => {
      map.flyTo([20, 0], 2, { duration: reducedMotion.matches ? 0 : 0.5, animate: !reducedMotion.matches }); // World view
      if (searchInput) searchInput.value = '';
      filterChips.forEach(c => c.classList.remove('active'));
      const allChip = document.querySelector('[data-region="all"]');
      if (allChip) allChip.classList.add('active');
      markers.forEach(m => m.addTo(map));
      renderDealerList(dealers);
    });
  }

  // Locate me
  if (locateBtn) {
    locateBtn.addEventListener('click', () => {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            if (lifetime.signal.aborted) return;
            map.flyTo([position.coords.latitude, position.coords.longitude], 8, { duration: reducedMotion.matches ? 0 : 0.5, animate: !reducedMotion.matches });
          },
          () => {
            if (!lifetime.signal.aborted) alert(strings.locationError);
          }
        );
      } else {
        alert(strings.locationUnsupported);
      }
    });
  }

  }
  window.LoftsailsInitDealerMap = init;
  if (!window.__loftsailsDealerMapEventsBound) {
    document.addEventListener('shopify:section:load', () => window.LoftsailsInitDealerMap());
    window.__loftsailsDealerMapEventsBound = true;
  }
  init();
})();
